import React from 'react'
import FormField from './FormField'
import NumberFormat from 'react-number-format'
import {Label} from '@cred/react-label'
import Font from '@cred/react-font'
import MaskedInput from 'react-text-mask'
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe'
import styles from './themes/default/styles.module.scss'

const textInput = ({
    input,
    onKeyDown,
    sensitive,
    label,
    meta,
    placeholder,
    autoComplete,
    type,
    getInputRef,
    readOnly,
    disabledField,
    list = '',
    icon = true,
    warning,
  }) =>
    <FormField status={meta} error={meta.error} warning={warning} value={input.value} disabledField={disabledField} label={label} icon={icon}>
      <Font element="div">
        <input ref={getInputRef} className={getSensitiveClass(sensitive)} type={type} autoComplete={autoComplete}
          onKeyDown={onKeyDown} placeholder={placeholder} readOnly={readOnly} disabled={disabledField} list={list}
          {...input}/>
      </Font>
      <Label>
        {label}
      </Label>
    </FormField>,

  zipInput = ({input, onKeyDown, sensitive, label, meta, placeholder, autoComplete, getInputRef}) =>
    <FormField status={meta} error={meta.error} value={input.value} label={label}>
      <Font element="div">
        <input type="tel" ref={getInputRef} className={getSensitiveClass(sensitive)}
          autoComplete={autoComplete}
          onKeyDown={onKeyDown} placeholder={placeholder} {...input}/>
      </Font>
      <Label>
        {label}
      </Label>
    </FormField>,

  select = ({input, isDisabled = false, label, meta, children, placeholder, icon = true}) =>
    <FormField status={meta} error={meta.error} placeholder={placeholder} value={input.value} label={label} icon={icon}>
      <Font element="select" disabled={isDisabled} {...input}>
        {children}
      </Font>
      <Label>
        {label}
      </Label>
    </FormField>,

  moneyInput = ({input, onKeyPress, label, meta, autoComplete, placeholder = '$', getInputRef, icon = true}) =>
    <FormField status={meta} error={meta.error} value={input.value} icon={icon} label={label}>
      <Font element="div">
        <NumberFormat
          thousandSeparator={true}
          prefix="$"
          decimalScale={0}
          allowNegative={false}
          type="tel"
          autoComplete={autoComplete}
          placeholder={placeholder}
          maxLength="10"
          onKeyPress={onKeyPress}
          getInputRef={getInputRef}
          {...input}
        />
      </Font>
      <Label>{label}</Label>
    </FormField>,

  numberInput = ({input, onKeyPress, label, meta, autoComplete, placeholder = '$', getInputRef, icon = true, thousandSeparator = ',', decimalScale, allowNegative = true, decimalSeparator = '.', fixedDecimalScale = false, mask, numberFormat}) =>
    <FormField status={meta} error={meta.error} value={input.value} icon={icon} label={label}>
      <Font element="div">
        <NumberFormat
          mask={mask}
          fixedDecimalScale={fixedDecimalScale}
          decimalSeparator={decimalSeparator}
          thousandSeparator={thousandSeparator}
          decimalScale={decimalScale}
          allowNegative={allowNegative}
          type="tel"
          autoComplete={autoComplete}
          placeholder={placeholder}
          onKeyPress={onKeyPress}
          format={numberFormat}
          getInputRef={getInputRef}
          {...input}
        />
      </Font>
      <Label>{label}</Label>
    </FormField>,

  singleLetterInput = ({input, label, meta, placeholder}) =>
    <FormField status={meta} error={meta.error} value={input.value} label={label}>
      <Font element="div">
        <MaskedInput mask={[/[a-zA-Z]/]} pipe={upperCasePipe} placeholder={placeholder} {...input}/>
      </Font>
      <Label>{label}</Label>
    </FormField>,

  upperCasePipe = conformedValue => conformedValue.toUpperCase(),

  autoCorrectedDatePipe = createAutoCorrectedDatePipe('mm/dd/yyyy'),

  dateInput = ({input, sensitive, label, meta, placeholder = '__/__/____', mask, getInputRef}) => {
    const render = (maskedInputRef, props) => <input ref={ref => {
      maskedInputRef(ref)
      getInputRef(ref)
    }} {...props}/>

    return (
      <FormField status={meta} error={meta.error} value={input.value} label={label}>
        <Font element="div">
          <MaskedInput render={render} className={getSensitiveClass(sensitive)} keepCharPositions={true} type="tel"
            placeholder={placeholder}
            mask={mask || [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
            pipe={autoCorrectedDatePipe} {...input}/>
        </Font>
        <Label>{label}</Label>
      </FormField>
    )
  },

  shortDateInput = ({input, sensitive, label, meta, placeholder = '__/____', mask}) =>
    <FormField status={meta} error={meta.error} value={input.value} label={label}>
      <Font element="div">
        <MaskedInput
          className={getSensitiveClass(sensitive)}
          keepCharPositions={true}
          type="tel"
          placeholder={placeholder}
          mask={mask || [/\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
          pipe={autoCorrectedDatePipe}
          {...input}
        />
      </Font>
      <Label>{label}</Label>
    </FormField>,

  phoneInput = ({input, sensitive, label, meta, placeholder, getInputRef}) => {
    const render = (maskedInputRef, props) => <input ref={ref => {
      maskedInputRef(ref)
      getInputRef(ref)
    }} {...props}/>

    return (
      <FormField status={meta} error={meta.error} value={input.value} label={label}>
        <Font element="div">
          <MaskedInput render={render} className={getSensitiveClass(sensitive)} type="tel" placeholder={placeholder}
            mask={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]} {...input}/>
        </Font>
        <Label>{label}</Label>
      </FormField>
    )
  },

  hiddenInput = ({input}) => <input {...input} type="hidden"/>,

  getSensitiveClass = sensitive =>
    sensitive ? 'inspectletIgnore' : '',

  renderError = ({meta: {touched, error}}) =>
    touched && error ? <Font type="bodySmall" color="error" className="mt-8">{error}</Font> : false

export {
  FormField,
  textInput,
  zipInput,
  select,
  moneyInput,
  singleLetterInput,
  dateInput,
  shortDateInput,
  phoneInput,
  upperCasePipe,
  getSensitiveClass,
  renderError,
  hiddenInput,
  numberInput
}
